/* eslint-disable no-unused-expressions */
import React, { useCallback, useMemo, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';

import Box from '@mui/material/Box';

import CopyIcon from '@mui/icons-material/FileCopy';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import LinkIcon from '@mui/icons-material/Link';

import ExternalItemLinkForm from '@ts-core/core/components/ExternalItemLinkForm';
import Dialog, { useDialog } from '@ts-core/components/Dialog';
import { Feature } from '@ts-core/utils/features';
import SpeedDialMenu from '@ts-core/components/SpeedDialMenu';

import FileUploader from 'core/components/FileUploader';

import MediaForm from 'modules/media/components/MediaForm';
import ImportSubjectItemsFromCourse from 'modules/subjects/components/ImportSubjectItemsFromCourse';

import { useAcademyAuthUtils } from 'modules/auth/hooks';

import MediaItemsTable from '../../components/MediaItemsTable';
import { useImportMediaItemsFromCourse, useMediaItem, useMediaItems, useMediaUploader } from '../../hooks';

const MediaPage = () => {
  const history = useHistory();
  const intl = useIntl();
  const { courseId, subjectId } = useParams();
  const mediaSelectorRef = useRef();
  //
  const { academyHasFeature } = useAcademyAuthUtils();

  const {
    onOpenDialog: onOpenImportMediaItemsDialog,
    onCloseDialog: onCloseImportMediaItemsDialog,
    dialogProps: importMediaItemsDialogProps,
  } = useDialog();

  const {
    onOpenDialog: onOpenCreateExternalVideoForm,
    onCloseDialog: onCloseCreateExternalVideoForm,
    dialogProps: createExternalVideoDialogProps,
  } = useDialog();

  const {
    mediaItems,
    loading,
    onReorderMediaItems,
    onDeleteMediaItem,
    onToggleMediaItem,
    onImportMediaItemsFromCourse,
    importingMediaItemsFromCourse,
  } = useMediaItems({
    courseId,
    subjectId,
    onMediaItemsImportedFromCourse: onCloseImportMediaItemsDialog,
  });

  /**
   * Navigation
   */
  const onSelectMediaItem = useCallback(
    (mediaItem) => {
      if (mediaItem.isExternal) {
        // eslint-disable-next-line no-undef
        window.open(mediaItem.externalUrl, mediaItem.name);
      } else {
        history.push(`media/${mediaItem._id}/player`);
      }
    },
    [history]
  );

  const handleOpenFileSelector = useCallback(() => {
    mediaSelectorRef?.current?.click();
  }, [mediaSelectorRef]);

  /**
   * Edit Media Item
   */
  const { onOpenDialog: onOpenEditMediaItemDialog, onCloseDialog: onCloseMediaItemDialog, dialogProps: mediaItemDialogProps } = useDialog();

  const {
    onUpdateMediaItem,
    updatingMediaItem,
    updateMediaItemError,
    onCreateExternalMediaItem,
    creatingExternalMediaItem,
    createExternalMediaItemError,
  } = useMediaItem({
    courseId,
    subjectId,
    onMediaItemUpdated: onCloseMediaItemDialog,
    onExternalMediaItemCreated: onCloseCreateExternalVideoForm,
  });
  const handleUpdateMediaItem = useCallback(
    (input) => {
      const { dialogData } = mediaItemDialogProps;
      onUpdateMediaItem(dialogData?.mediaItem._id, input);
    },
    [mediaItemDialogProps, onUpdateMediaItem]
  );

  const handleOpenEditMediaItemDialog = useCallback(
    (mediaItem) => {
      onOpenEditMediaItemDialog({
        title: intl.formatMessage({ id: 'dialog.editVideo.title' }),
        dialogData: { mediaItem },
      });
    },
    [intl, onOpenEditMediaItemDialog]
  );

  /**
   * Import Media items
   */

  const handleOpenImportMediaItems = useCallback(() => {
    onOpenImportMediaItemsDialog({
      title: intl.formatMessage({ id: 'dialog.importVideos.title' }),
    });
  }, [onOpenImportMediaItemsDialog, intl]);

  const handleImportMediaItems = useCallback(
    (selectedMediaItems) => {
      onImportMediaItemsFromCourse({
        videos: selectedMediaItems.map((m) => m._id),
      });
    },
    [onImportMediaItemsFromCourse]
  );

  const importMediaItemsProps = useImportMediaItemsFromCourse({
    courseId,
    importingMediaItems: importingMediaItemsFromCourse,
    onConfirmSelection: handleImportMediaItems,
  });

  const mediaUploaderProps = useMediaUploader({ courseId, subjectId });

  /**
   * Create External Video
   */

  const handleOpenCreateExternalVideo = useCallback(() => {
    onOpenCreateExternalVideoForm({
      title: intl.formatMessage({ id: 'button.addVideoLink.title' }),
    });
  }, [intl, onOpenCreateExternalVideoForm]);

  const speedDialOptions = useMemo(() => {
    const result = [];

    if (academyHasFeature(Feature.ExternalVideos)) {
      result.push({
        icon: <LinkIcon />,
        title: intl.formatMessage({ id: 'button.addVideoLink.title' }),
        onClick: handleOpenCreateExternalVideo,
      });
    }

    if (academyHasFeature(Feature.VideoOnDemand)) {
      result.push(
        {
          icon: <OndemandVideoIcon />,
          title: intl.formatMessage({ id: 'button.addVideo.title' }),
          onClick: handleOpenFileSelector,
        },
        {
          icon: <CopyIcon />,
          title: intl.formatMessage({ id: 'dialog.importVideos.title' }),
          onClick: handleOpenImportMediaItems,
        }
      );
    }

    return result;
  }, [intl, handleOpenFileSelector, handleOpenImportMediaItems, handleOpenCreateExternalVideo, academyHasFeature]);

  return (
    <Box position="relative">
      <Dialog {...mediaItemDialogProps}>
        {(data) => (
          <MediaForm media={data.mediaItem} onSubmit={handleUpdateMediaItem} submitting={updatingMediaItem} submitError={updateMediaItemError} />
        )}
      </Dialog>
      <Dialog {...importMediaItemsDialogProps}>
        <ImportSubjectItemsFromCourse {...importMediaItemsProps} />
      </Dialog>
      <Dialog {...createExternalVideoDialogProps}>
        <ExternalItemLinkForm
          onSubmit={onCreateExternalMediaItem}
          isSubmitting={creatingExternalMediaItem}
          submitError={createExternalMediaItemError}
        />
      </Dialog>
      {academyHasFeature(Feature.VideoOnDemand) && <FileUploader ref={mediaSelectorRef} {...mediaUploaderProps} />}
      <MediaItemsTable
        mediaItems={mediaItems}
        loading={loading}
        onToggleMediaItem={onToggleMediaItem}
        onSelectMediaItem={onSelectMediaItem}
        onEditMediaItem={handleOpenEditMediaItemDialog}
        onReorderMediaItems={onReorderMediaItems}
        onRemoveMediaItem={onDeleteMediaItem}
      />
      <SpeedDialMenu options={speedDialOptions} />
    </Box>
  );
};

export default MediaPage;
